<template>
  <div class="main row">
    <div class="col-md-12">
      <KTCodePreview v-bind:title="'Chi tiết yêu cầu bổ sung NS'">
        <template v-slot:toolbar>
          <!-- Sửa: DRAFT hoặc PENDING (người tạo) -->
          <b-button
            v-if="request.canEdit"
            variant="primary"
            size="sm"
            class="mr-2"
            @click="editRequest"
          >
            <i class="fas fa-edit"></i> Sửa
          </b-button>

          <!-- DRAFT actions -->
          <template v-if="request.status === BUDGET_LINE_REQUEST_STATUS.DRAFT">
            <b-button
              variant="success"
              size="sm"
              class="mr-2"
              @click="showSubmitModal"
            >
              <i class="fas fa-paper-plane"></i> Gửi duyệt
            </b-button>
            <b-button
              variant="outline-danger"
              size="sm"
              class="mr-2"
              @click="confirmDelete"
            >
              <i class="fas fa-trash-alt"></i> Xóa
            </b-button>
          </template>

          <!-- PENDING/BROWSING + canApprove actions -->
          <template v-if="(request.status === BUDGET_LINE_REQUEST_STATUS.PENDING || request.status === BUDGET_LINE_REQUEST_STATUS.BROWSING) && request.canApprove">
            <b-button
              variant="success"
              size="sm"
              class="mr-2"
              @click="showApproveModal"
            >
              <i class="fas fa-check"></i> Duyệt
            </b-button>
            <b-button
              variant="danger"
              size="sm"
              class="mr-2"
              @click="showRejectModal"
            >
              <i class="fas fa-times"></i> Không duyệt
            </b-button>
          </template>

          <!-- Hoàn duyệt -->
          <template v-if="request.canUndo">
            <b-button
              variant="warning"
              size="sm"
              class="mr-2"
              @click="showUndoModal"
            >
              <i class="fas fa-undo"></i> Hoàn duyệt
            </b-button>
          </template>

          <b-button variant="secondary" size="sm" class="mr-2" @click="showHistoryModal">
            <i class="flaticon2-time"></i>&nbsp; Lịch sử
          </b-button>
          <b-button variant="secondary" size="sm" @click="goBack">
            <i class="fas fa-arrow-left"></i> Quay lại
          </b-button>
        </template>

        <template v-slot:preview>
          <div class="blr-detail-layout" ref="splitLayout">
            <div class="blr-detail-layout__main" :style="{ width: splitMainWidth }">
              <!-- Header info -->
              <b-row class="mb-4">
                <b-col cols="3">
                  <small class="text-muted">Mã phiếu</small>
                  <div><strong>{{ request.code || '—' }}</strong></div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Trạng thái</small>
                  <div>
                    <b-badge :variant="statusVariant(request.status)">
                      {{ statusName(request.status) }}
                    </b-badge>
                  </div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Phòng ban</small>
                  <div>{{ request.departmentName || request.departmentId }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Cửa hàng</small>
                  <div>{{ request.storeName || 'Cấp phòng ban' }}</div>
                </b-col>
              </b-row>

              <b-row class="mb-4">
                <b-col cols="3">
                  <small class="text-muted">Khoản mục</small>
                  <div>{{ request.costItemName || request.costItemId }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Tổng số tiền</small>
                  <div>
                    <strong class="text-primary">{{ formatAmount(request.totalAmount) }} đ</strong>
                    <small class="text-muted ml-1" v-if="request.periods && request.periods.length">
                      ({{ request.periods.length }} kỳ)
                    </small>
                  </div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Người tạo</small>
                  <div>{{ request.createdByName }}</div>
                </b-col>
                <b-col cols="3">
                  <small class="text-muted">Ngày tạo</small>
                  <div>{{ formatDateTime(request.createdAt) }}</div>
                </b-col>
              </b-row>

              <!-- Bảng kỳ + số tiền (read-only) -->
              <b-row class="mb-4" v-if="request.periods && request.periods.length">
                <b-col cols="12">
                  <small class="text-muted d-block mb-1">Phân bổ theo kỳ</small>
                  <b-table-simple small bordered responsive class="period-table mb-1">
                    <b-thead>
                      <b-tr>
                        <b-th style="width:40px" class="text-center">#</b-th>
                        <b-th style="width:110px">Kỳ</b-th>
                        <b-th class="text-right">Số tiền yêu cầu (đ)</b-th>
                        <b-th class="text-right">Ngân sách hiện tại (đ)</b-th>
                        <b-th class="text-right">Đã sử dụng (đ)</b-th>
                        <b-th class="text-right">Còn lại (đ)</b-th>
                        <b-th style="width:130px" class="text-center">Trạng thái NS</b-th>
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr v-for="(p, idx) in request.periods" :key="`period-${idx}`">
                        <b-td class="text-center">{{ idx + 1 }}</b-td>
                        <b-td>{{ p.month }}/{{ p.year }}</b-td>
                        <b-td class="text-right">{{ formatAmount(p.amount) }}</b-td>
                        <template v-if="p.currentBudget">
                          <b-td
                            class="text-right"
                            :title="p.currentBudget.yearBudgetLineId ? 'Xem chi tiết ngân sách năm (tab mới)' : `Tổng ngân sách phòng ban ${request.departmentName || ''} cho kỳ ${p.month}/${p.year}`"
                          >
                            <a
                              v-if="p.currentBudget.yearBudgetLineId"
                              :href="buildYearBudgetLineHref(p.currentBudget.yearBudgetLineId)"
                              target="_blank"
                              rel="noopener"
                              class="year-budget-link"
                            >
                              {{ formatAmount(p.currentBudget.allocatedAmount) }}
                              <i class="fas fa-external-link-alt fa-sm ml-1"></i>
                            </a>
                            <template v-else>
                              {{ formatAmount(p.currentBudget.allocatedAmount) }}
                            </template>
                          </b-td>
                          <b-td class="text-right">{{ formatAmount(p.currentBudget.usedAmount) }}</b-td>
                          <b-td class="text-right" :class="remainingClass(p.currentBudget.remainingAmount)">
                            {{ formatAmount(p.currentBudget.remainingAmount) }}
                          </b-td>
                          <b-td class="text-center">
                            <b-badge :variant="budgetStatusVariant(p.currentBudget.status)">
                              {{ budgetStatusName(p.currentBudget.status) }}
                            </b-badge>
                          </b-td>
                        </template>
                        <template v-else>
                          <b-td class="text-right cell-muted">—</b-td>
                          <b-td class="text-right cell-muted">—</b-td>
                          <b-td class="text-right cell-muted">—</b-td>
                          <b-td class="text-center">
                            <b-badge variant="secondary">Chưa có</b-badge>
                          </b-td>
                        </template>
                      </b-tr>
                    </b-tbody>
                    <b-tfoot>
                      <b-tr>
                        <b-td colspan="2" class="text-right">
                          <strong>Tổng cộng</strong>
                        </b-td>
                        <b-td class="text-right">
                          <strong class="text-primary">{{ formatAmount(request.totalAmount) }}</strong>
                        </b-td>
                        <b-td class="text-right">
                          <strong>{{ formatAmount(periodsTotals.allocated) }}</strong>
                        </b-td>
                        <b-td class="text-right">
                          <strong>{{ formatAmount(periodsTotals.used) }}</strong>
                        </b-td>
                        <b-td class="text-right" :class="remainingClass(periodsTotals.remaining)">
                          <strong>{{ formatAmount(periodsTotals.remaining) }}</strong>
                        </b-td>
                        <b-td></b-td>
                      </b-tr>
                    </b-tfoot>
                  </b-table-simple>
                </b-col>
              </b-row>

              <b-row v-if="request.reason" class="mb-4">
                <b-col cols="12" md="8">
                  <small class="text-muted">Lý do yêu cầu</small>
                  <div>{{ request.reason }}</div>
                </b-col>
              </b-row>

              <!-- Link ngân sách nếu đã duyệt -->
              <b-row
                v-if="request.status === BUDGET_LINE_REQUEST_STATUS.APPROVED && request.budgetLineId"
                class="mb-4"
              >
                <b-col cols="12">
                  <b-alert variant="success" show>
                    Yêu cầu đã được duyệt.
                    <router-link
                      :to="{ name: 'detail-budget-line', query: { id: request.budgetLineId } }"
                      class="alert-link ml-2"
                    >
                      Xem ngân sách
                    </router-link>
                  </b-alert>
                </b-col>
              </b-row>

              <!-- Tệp đính kèm -->
              <b-row class="mb-4">
                <b-col cols="12">
                  <small class="text-muted d-block mb-1">Tệp đính kèm</small>
                  <FileUploadManager
                    v-if="request.id"
                    :entity-id="request.id"
                    :entity="UPLOAD_ENTITY.BUDGET_LINE_REQUEST"
                    :disabled="true"
                  />
                </b-col>
              </b-row>

              <!-- Approval summary -->
              <b-row class="mb-4">
                <b-col cols="12" md="6">
                  <PurchaseRequestApprovalSummary :approvers="request.approvers || []" />
                </b-col>
              </b-row>
            </div>

            <div
              class="blr-detail-layout__resizer"
              :class="{ 'is-active': isSplitResizing }"
              role="separator"
              aria-orientation="vertical"
              aria-label="Điều chỉnh độ rộng cột"
              @mousedown.prevent="startSplitResize"
            />

            <div class="blr-detail-layout__aside">
              <CommentSection
                v-if="request.id"
                :module="COMMENT_MODULE.BUDGET_LINE_REQUEST"
                :reference-id="request.id"
                embedded
              />
            </div>
          </div>

          <!-- Modal submit -->
          <b-modal
            ref="submitModal"
            title="Gửi duyệt yêu cầu"
            @ok="confirmSubmit"
            ok-variant="success"
            ok-title="Gửi duyệt"
            cancel-title="Đóng"
          >
            <p>Bạn có chắc muốn gửi duyệt yêu cầu này để chờ duyệt?</p>
          </b-modal>

          <!-- Modal duyệt -->
          <b-modal
            ref="approveModal"
            title="Duyệt yêu cầu bổ sung"
            @ok="confirmApprove"
            ok-variant="success"
            ok-title="Duyệt"
            cancel-title="Đóng"
          >
            <b-form-group label="Ghi chú (nếu có)">
              <b-form-textarea
                v-model="approveForm.note"
                rows="3"
                placeholder="Nhập ghi chú"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal không duyệt -->
          <b-modal
            ref="rejectModal"
            title="Không duyệt yêu cầu bổ sung"
            @ok="confirmReject"
            ok-variant="danger"
            ok-title="Không duyệt"
            cancel-title="Đóng"
          >
            <b-form-group>
              <template #label>
                Lý do <span class="text-danger">*</span>
              </template>
              <b-form-textarea
                v-model="rejectForm.note"
                rows="3"
                placeholder="Nhập lý do không duyệt"
                required
              />
            </b-form-group>
          </b-modal>

          <PurchaseRequestHistoryModal
            ref="historyModal"
            :histories="histories"
          />

          <!-- Modal hoàn duyệt -->
          <b-modal
            ref="undoModal"
            title="Hoàn duyệt yêu cầu bổ sung"
            @ok="confirmUndo"
            ok-variant="warning"
            ok-title="Hoàn duyệt"
            cancel-title="Đóng"
          >
            <p>Bạn có chắc muốn hoàn lại bước duyệt này?</p>
            <b-form-group label="Ghi chú (nếu có)">
              <b-form-textarea
                v-model="undoForm.note"
                rows="3"
                placeholder="Nhập ghi chú"
              />
            </b-form-group>
          </b-modal>

          <!-- Modal cảnh báo hoàn duyệt bị chặn -->
          <RevokeBlockedModal
            ref="revokeBlockedModal"
            :blocked-periods="request.blockedPeriods || []"
          />
        </template>
      </KTCodePreview>
    </div>
  </div>
</template>

<script>
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ApiService from '@/core/services/api.service';
import { makeToastSuccess, makeToastFaile, convertPrice, handleError } from '@/utils/common';
import PurchaseRequestApprovalSummary from '../purchase-requests/components/PurchaseRequestApprovalSummary.vue';
import PurchaseRequestHistoryModal from '../purchase-requests/components/PurchaseRequestHistoryModal.vue';
import CommentSection from '@/view/components/comment/CommentSection.vue';
import FileUploadManager from '@/view/modules/upload/FileUploadManager.vue';
import RevokeBlockedModal from './components/RevokeBlockedModal.vue';
import BudgetLineRequestService from '@/services/budget-line-request.service';
import moment from 'moment';
import {
  BUDGET_LINE_REQUEST_STATUS,
  BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT,
  BUDGET_STATUS,
  WORKFLOW_APPROVAL_STEP_STATUS,
  COMMENT_MODULE,
  UPLOAD_ENTITY,
} from '@/utils/enum';
import { BUDGET_LINE_REQUEST_STATUS_NAME, HISTORY_ACTION_TYPE_CODE } from '@/utils/enum-name';

// Map BUDGET_STATUS → label tiếng Việt (theo SRS t101354)
// Lưu ý: BUDGET_STATUS_NAME global đang dùng "Chờ duyệt" cho DRAFT (cho list view)
// — ở đây spec yêu cầu "Nháp" + bổ sung "Đã khóa" (LOCKED) nên định nghĩa riêng.
const BUDGET_STATUS_LABEL = Object.freeze({
  [BUDGET_STATUS.DRAFT]: 'Nháp',
  [BUDGET_STATUS.APPROVED]: 'Đã duyệt',
  [BUDGET_STATUS.LOCKED]: 'Đã khóa',
  [BUDGET_STATUS.REJECTED]: 'Từ chối',
  [BUDGET_STATUS.BROWSING]: 'Đang duyệt',
});

const BUDGET_STATUS_VARIANT = Object.freeze({
  [BUDGET_STATUS.DRAFT]: 'secondary',
  [BUDGET_STATUS.APPROVED]: 'success',
  [BUDGET_STATUS.LOCKED]: 'dark',
  [BUDGET_STATUS.REJECTED]: 'danger',
  [BUDGET_STATUS.BROWSING]: 'info',
});

export default {
  components: {
    KTCodePreview,
    PurchaseRequestApprovalSummary,
    PurchaseRequestHistoryModal,
    CommentSection,
    FileUploadManager,
    RevokeBlockedModal,
  },
  beforeRouteEnter(_to, from, next) {
    next((vm) => { vm.returnQuery = from && from.name === 'list-budget-line-requests' ? { ...from.query } : {}; });
  },
  data() {
    return {
      BUDGET_LINE_REQUEST_STATUS,
      COMMENT_MODULE,
      UPLOAD_ENTITY,
      returnQuery: {},
      request: {},
      histories: [],
      approveForm: { note: '' },
      rejectForm: { note: '' },
      undoForm: { note: '' },
      splitLeftPercent: 70,
      isSplitResizing: false,
    };
  },
  computed: {
    splitMainWidth() {
      return `${this.splitLeftPercent}%`;
    },
    periodsTotals() {
      const periods = Array.isArray(this.request.periods) ? this.request.periods : [];
      return periods.reduce(
        (acc, p) => {
          const cb = p && p.currentBudget;
          acc.allocated += cb ? Number(cb.allocatedAmount) || 0 : 0;
          acc.used += cb ? Number(cb.usedAmount) || 0 : 0;
          acc.remaining += cb ? Number(cb.remainingAmount) || 0 : 0;
          return acc;
        },
        { allocated: 0, used: 0, remaining: 0 },
      );
    },
  },
  created() {
    const id = this.$route.query.id;
    if (id) {
      this.fetchDetail(id);
    }
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Kế toán', route: '#' },
      { title: 'Yêu cầu bổ sung NS', route: '/budget/budget-line-requests' },
      { title: 'Chi tiết' },
    ]);
    document.addEventListener('mousemove', this.onSplitResize);
    document.addEventListener('mouseup', this.stopSplitResize);
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onSplitResize);
    document.removeEventListener('mouseup', this.stopSplitResize);
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  },
  methods: {
    makeToastSuccess,
    makeToastFaile,
    convertPrice,
    formatPeriod(val) {
      return val ? moment(val).format('MM/YYYY') : '';
    },
    formatDateTime(val) {
      return val ? moment(val).format('DD/MM/YYYY HH:mm') : '';
    },
    statusVariant(val) {
      return BUDGET_LINE_REQUEST_STATUS_BADGE_VARIANT[val] || 'secondary';
    },
    statusName(val) {
      return BUDGET_LINE_REQUEST_STATUS_NAME[val] || '';
    },
    historyActionLabel(action) {
      return HISTORY_ACTION_TYPE_CODE[action] || String(action || '');
    },
    formatAmount(val) {
      const n = Number(val) || 0;
      return n.toLocaleString('vi-VN');
    },
    budgetStatusName(status) {
      if (status == null) return 'Chưa có';
      return BUDGET_STATUS_LABEL[status] || '—';
    },
    budgetStatusVariant(status) {
      if (status == null) return 'secondary';
      return BUDGET_STATUS_VARIANT[status] || 'secondary';
    },
    remainingClass(val) {
      const n = Number(val) || 0;
      if (n > 0) return 'cell-remaining-positive';
      if (n < 0) return 'cell-remaining-negative';
      return '';
    },
    /**
     * t101354 — build href tới màn detail BudgetLine năm (parent),
     * dùng $router.resolve để giữ đúng base URL của Vue Router (history mode).
     */
    buildYearBudgetLineHref(yearBudgetLineId) {
      if (!yearBudgetLineId) return '#';
      const route = this.$router.resolve({
        name: 'detail-budget-year-group',
        query: { id: yearBudgetLineId },
      });
      return (route && route.href) || `/budget/budget-lines/year-detail?id=${yearBudgetLineId}`;
    },
    fetchDetail(id) {
      // Dùng service layer (transform raw → flat shape — Rule 8)
      return BudgetLineRequestService.getById(id)
        .then((transformed) => {
          this.request = transformed || {};
          this.histories = (this.request.history || []).map((h) => ({
            ...h,
            actionLabel: this.historyActionLabel(h.action),
          }));
          return transformed;
        })
        .catch((err) => handleError(err));
    },
    showHistoryModal() {
      this.$refs.historyModal?.open();
    },
    editRequest() {
      const id = this.$route.query.id;
      this.$router.push({
        name: 'upsert-budget-line-request',
        query: { id, from: this.$route.fullPath },
      });
    },
    showSubmitModal() {
      this.$refs.submitModal.show();
    },
    confirmSubmit(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.post(`v2/budget-line-requests/${id}/submit`, {})
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Submit thành công');
          this.$refs.submitModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    showApproveModal() {
      this.approveForm.note = '';
      this.$refs.approveModal.show();
    },
    confirmApprove(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.APPROVED,
        note: this.approveForm.note || '',
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Đã duyệt thành công');
          this.$refs.approveModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    showRejectModal() {
      this.rejectForm.note = '';
      this.$refs.rejectModal.show();
    },
    confirmReject(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      if (!this.rejectForm.note || !this.rejectForm.note.trim()) {
        this.makeToastFaile('Vui lòng nhập lý do không duyệt!');
        return;
      }
      const id = this.$route.query.id;
      ApiService.patch(`v2/budget-line-requests/${id}/change-status`, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REJECTED,
        note: this.rejectForm.note,
      })
        .then(({ data }) => {
          this.makeToastSuccess(data.message || 'Đã từ chối thành công');
          this.$refs.rejectModal.hide();
          this.fetchDetail(id);
        })
        .catch((err) => handleError(err));
    },
    async showUndoModal() {
      this.undoForm.note = '';
      const id = this.$route.query.id;
      // GET detail lại để lấy blockedPeriods mới nhất trước khi mở modal
      try {
        const fresh = await BudgetLineRequestService.getById(id);
        if (fresh) {
          this.request = fresh;
          if (Array.isArray(fresh.blockedPeriods) && fresh.blockedPeriods.length > 0) {
            // Có kỳ bị chặn — show modal cảnh báo, không cho confirm
            this.$nextTick(() => {
              this.$refs.revokeBlockedModal && this.$refs.revokeBlockedModal.open();
            });
            return;
          }
        }
      } catch (err) {
        handleError(err);
        return;
      }
      // Sạch — mở modal confirm thường
      this.$refs.undoModal.show();
    },
    confirmUndo(evt) {
      if (evt && evt.preventDefault) evt.preventDefault();
      const id = this.$route.query.id;
      BudgetLineRequestService.changeStatus(id, {
        status: WORKFLOW_APPROVAL_STEP_STATUS.REFUND_APPROVED,
        note: this.undoForm.note || '',
      })
        .then(({ message, data }) => {
          this.makeToastSuccess(message || 'Hoàn duyệt thành công');
          this.$refs.undoModal.hide();
          if (data) this.request = data;
          this.fetchDetail(id);
        })
        .catch((err) => {
          // Nếu BE phản hồi CANNOT_REVOKE_PERIODS thì show modal blocked
          const beCode = err && err.response && err.response.data && err.response.data.code;
          const blockedFromBE =
            err && err.response && err.response.data && err.response.data.details
              ? err.response.data.details.blockedPeriods
              : null;
          if (beCode === 'CANNOT_REVOKE_PERIODS' && Array.isArray(blockedFromBE)) {
            this.$set(this.request, 'blockedPeriods', blockedFromBE);
            this.$refs.undoModal.hide();
            this.$nextTick(() => {
              this.$refs.revokeBlockedModal && this.$refs.revokeBlockedModal.open();
            });
            return;
          }
          handleError(err);
        });
    },
    confirmDelete() {
      this.$bvModal
        .msgBoxConfirm('Bạn có chắc muốn xóa yêu cầu này?', {
          title: 'Xác nhận xóa',
          okVariant: 'danger',
          okTitle: 'Xóa',
          cancelTitle: 'Đóng',
        })
        .then((confirmed) => {
          if (!confirmed) return;
          const id = this.$route.query.id;
          ApiService.delete(`v2/budget-line-requests/${id}`)
            .then(({ data }) => {
              this.makeToastSuccess(data.message || 'Đã xóa thành công');
              this.$router.push({ name: 'list-budget-line-requests', query: { ...this.returnQuery } });
            })
            .catch((err) => handleError(err));
        });
    },
    goBack() {
      this.$router.push({ name: 'list-budget-line-requests', query: { ...this.returnQuery } }).catch(() => {});
    },
    startSplitResize() {
      this.isSplitResizing = true;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
    },
    onSplitResize(event) {
      if (!this.isSplitResizing || !this.$refs.splitLayout) return;
      const rect = this.$refs.splitLayout.getBoundingClientRect();
      if (!rect.width) return;
      const nextPercent = ((event.clientX - rect.left) / rect.width) * 100;
      this.splitLeftPercent = Math.min(85, Math.max(50, nextPercent));
    },
    stopSplitResize() {
      if (!this.isSplitResizing) return;
      this.isSplitResizing = false;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    },
  },
};
</script>

<style scoped>
.period-table th,
.period-table td {
  vertical-align: middle;
}

.period-table .cell-muted {
  color: #6c757d;
}

.period-table .year-budget-link {
  color: #3699ff;
  cursor: pointer;
  text-decoration: none;
}

.period-table .year-budget-link:hover,
.period-table .year-budget-link:focus {
  color: #187de4;
  text-decoration: underline;
}

.period-table .year-budget-link .fa-external-link-alt {
  opacity: 0.7;
  font-size: 0.75em;
}

.period-table .cell-remaining-positive {
  color: #28a745;
}

.period-table .cell-remaining-negative {
  color: #dc3545;
  font-weight: 600;
}

.blr-detail-layout {
  display: flex;
  align-items: stretch;
  gap: 0;
  min-height: calc(100vh - 180px);
}

.blr-detail-layout__main {
  flex: 0 0 auto;
  min-width: 0;
  padding-right: 12px;
}

.blr-detail-layout__resizer {
  flex: 0 0 10px;
  margin: 0 2px;
  border-radius: 999px;
  background: rgba(0, 0, 0, 0.08);
  cursor: col-resize;
  transition: background 0.15s ease;
  overflow: hidden;
}

.blr-detail-layout__resizer:hover,
.blr-detail-layout__resizer.is-active {
  background: rgba(54, 153, 255, 0.35);
}

.blr-detail-layout__aside {
  flex: 1 1 auto;
  min-width: 220px;
  min-height: 0;
  height: calc(100vh - 180px);
  max-height: calc(100vh - 180px);
  padding-left: 12px;
  border-left: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
</style>
